import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  LinkOutlined,
  ReloadOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import axiosAuth from "../utils/axiosAuth";
import "./styles/StormOps.css";

function formatDate(value) {
  if (!value) return "Not recorded";
  if (value._seconds) return new Date(value._seconds * 1000).toLocaleString();
  if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not recorded" : date.toLocaleString();
}

function statusLabel(run) {
  if (run?.status === "completed") return "completed";
  if (run?.stormOps?.productionStatus === "queued") return "queued";
  if (run?.stormOps?.productionStatus === "running") return "running";
  if (run?.paymentStatus === "paid") return "paid";
  return "awaiting payment";
}

function publicPath(slug) {
  return slug ? `/public/stormops/${slug}` : "";
}

export default function StormOpsCampaignDetail() {
  const { campaignId } = useParams();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRun = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const response = await axiosAuth.get(`/api/stormops/runs/${campaignId}`);
      setRun(response.data?.data?.run || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Unable to load this StormOps run");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchRun();
    const interval = setInterval(fetchRun, 15000);
    return () => clearInterval(interval);
  }, [fetchRun]);

  const slug = run?.stormOps?.publicSlug;
  const publicPage = run?.stormOps?.publicPage;
  const kit = run?.stormOps?.kit;
  const monitor = run?.stormOps?.monitoring;
  const lifecycle = Array.isArray(run?.stormOps?.lifecycle) ? run.stormOps.lifecycle : [];
  const status = statusLabel(run);

  return (
    <main className="storm-shell storm-shell-workspace">
      <header className="storm-nav">
        <Link className="storm-brand" to="/">
          <span className="storm-brand-mark">S</span>
          <span>StormOps Dispatch</span>
        </Link>
        <div className="storm-nav-actions">
          <Link className="storm-icon-link" to="/campaigns">History</Link>
          <button className="storm-icon-button" type="button" onClick={fetchRun} disabled={loading} aria-label="Refresh campaign">
            <ReloadOutlined />
          </button>
        </div>
      </header>

      <section className="storm-detail-layout">
        <div className="storm-detail-main">
          <div className="storm-section-heading">
            <div>
              <p className="storm-kicker">Campaign Detail</p>
              <h1>{run?.stormOps?.serviceType || run?.serviceType || "StormOps run"} in {run?.stormOps?.zip || run?.searchTerm || "ZIP"}</h1>
            </div>
            <span className={`storm-status storm-status-${status.replace(/\s+/g, "-")}`}>{status}</span>
          </div>

          {error && <div className="storm-alert storm-alert-error">{error}</div>}
          {loading && !run && <div className="storm-empty-history"><SafetyOutlined /><p>Loading campaign detail.</p></div>}

          {run && (
            <>
              <div className="storm-detail-summary">
                <div>
                  <span>Payment</span>
                  <strong>{run.paymentStatus || "unpaid"}</strong>
                </div>
                <div>
                  <span>Progress</span>
                  <strong>{run.percentComplete || 0}%</strong>
                </div>
                <div>
                  <span>Created</span>
                  <strong>{formatDate(run.createdAt)}</strong>
                </div>
              </div>

              {run.paymentStatus !== "paid" && (
                <div className="storm-payment-banner">
                  <div>
                    <strong>GBP 49 StormOps response run</strong>
                    <span>Activates the response kit, public page and monitor for this campaign.</span>
                  </div>
                  <Link className="storm-primary storm-link-button" to={`/payment/${run.id}`}>
                    <CreditCardOutlined />
                    Activate Run
                  </Link>
                </div>
              )}

              <section className="storm-detail-section">
                <p className="storm-kicker">Official Alert Preview</p>
                <h2>{run.stormOps?.preview?.recommendedDirection || "Preview ready"}</h2>
                <p>{run.stormOps?.preview?.sourceLabel}</p>
                <div className="storm-run-facts">
                  <span>{run.stormOps?.preview?.location?.city}, {run.stormOps?.preview?.location?.state}</span>
                  <span>{run.stormOps?.preview?.alertCount || 0} active alerts</span>
                  <span>Source hash: {run.stormOps?.sourceArtifacts?.normalizedHash?.slice(0, 16) || "pending"}</span>
                </div>
              </section>

              {kit && (
                <section className="storm-detail-section">
                  <p className="storm-kicker">Response Kit</p>
                  <h2>{kit.customerNotice?.headline}</h2>
                  <p>{kit.customerNotice?.body}</p>
                  <ul className="storm-check-list">
                    {(kit.operationsChecklist || []).map((item) => (
                      <li key={item}><CheckCircleOutlined /> {item}</li>
                    ))}
                  </ul>
                </section>
              )}

              {publicPage && (
                <section className="storm-detail-section">
                  <p className="storm-kicker">Public Status Page</p>
                  <h2>{publicPage.title}</h2>
                  <p>{publicPage.notice}</p>
                  <Link className="storm-secondary storm-link-button" to={publicPath(slug)}>
                    <LinkOutlined />
                    Open Public Page
                  </Link>
                </section>
              )}

              {monitor && (
                <section className="storm-detail-section">
                  <p className="storm-kicker">Follow-Up Monitor</p>
                  <h2>{monitor.active ? "Monitoring is active" : "Monitoring is not active yet"}</h2>
                  <p>Last official-alert refresh: {formatDate(monitor.lastCycleAt)}</p>
                  <div className="storm-run-facts">
                    <span>Status: {monitor.lastCycleStatus || "pending"}</span>
                    <span>Cycles: {monitor.cycles?.length || 0}</span>
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <aside className="storm-detail-side">
          <div className="storm-section-heading">
            <div>
              <p className="storm-kicker">History</p>
              <h2>Lifecycle</h2>
            </div>
            <HistoryOutlined />
          </div>
          {lifecycle.length === 0 ? (
            <div className="storm-empty-history"><SafetyOutlined /><p>No lifecycle events yet.</p></div>
          ) : (
            <div className="storm-timeline">
              {lifecycle.map((event, index) => (
                <article key={`${event.stage}-${index}`}>
                  <strong>{String(event.stage || "event").replace(/_/g, " ")}</strong>
                  <span>{formatDate(event.at)}</span>
                </article>
              ))}
            </div>
          )}
        </aside>
      </section>
    </main>
  );
}
